import type { PlatformRetention } from '@sentinel/shared';
import { StateBadge, Table, type Column } from '@sentinel/ui';
import { formatDateTime, formatNumber } from '@sentinel/web-core';
import { useTranslation } from 'react-i18next';

const K = 'console.settings.retention';

type Period = PlatformRetention['periods'][number];

/** The last platform run: when, in which mode, and per category how many rows it touched (or
 *  would touch in preview) against the platform's period. */
export function PlatformLastRun({ overview }: { overview: PlatformRetention }) {
  const { t } = useTranslation();
  const last = overview.lastRun;
  if (last === null) return <p className="note">{t(`${K}.noRun`)}</p>;
  const columns: Column<Period>[] = [
    {
      key: 'category',
      header: t(`${K}.columns.category`),
      render: (p) => t(`${K}.categories.${p.category}`),
    },
    {
      key: 'period',
      header: t(`${K}.columns.period`),
      render: (p) => t(`${K}.units.${p.unit}`, { count: p.amount }),
    },
    {
      key: 'count',
      header: t(`${K}.columns.count`),
      mono: true,
      // Not counted in that run: a dash, never a zero that was not measured (rule 13).
      render: (p) => {
        const count = last.counts[p.category];
        return count === undefined ? t('common.labels.dash') : formatNumber(count);
      },
    },
  ];
  return (
    <div className="stack">
      <p style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <b>{t(`${K}.lastRun`)}</b>
        <span className="mono">{formatDateTime(last.finishedAt ?? last.startedAt)}</span>
        {last.failed ? (
          <StateBadge tone="crit" label={t(`${K}.failed`)} />
        ) : (
          <StateBadge
            tone={last.mode === 'active' ? 'ok' : 'info'}
            label={t(`${K}.ran.${last.mode}`)}
          />
        )}
      </p>
      <Table
        caption={t(`${K}.lastRun`)}
        columns={columns}
        rows={overview.periods}
        rowKey={(p) => p.category}
        compact
      />
    </div>
  );
}
